import React, { PropsWithChildren } from 'react';
import clsx from 'clsx';
import {
  Body,
  BodyProps,
  Caption,
  CaptionProps,
  Card,
  Grid,
  Tag,
  TagProps
} from '@walmart-web/livingdesign-components';

import styles from './styles.module.css';

type PropsWithoutTag = {
  showTag?: false;
};

type PropsWithTag = {
  showTag: true;
  tagProps: Omit<TagProps, 'children'> & { text: string };
};

type Props = {
  imgSrc: string;
  status: string;
  extraClasses?: string;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
} & (PropsWithTag | PropsWithoutTag);

const hasTagProps = (restProps: any): restProps is PropsWithTag =>
  'tagProps' in restProps;

function StatusCard({
  imgSrc,
  status,
  showTag = false,
  extraClasses = '',
  onClick,
  children,
  ...restProps
}: PropsWithChildren<Props>) {
  return (
    <Card className={clsx(styles.card, extraClasses)} onClick={onClick}>
      <img
        src={imgSrc}
        alt={`Status ${status} Image`}
        height="80px"
        width="80px"
      />
      <Grid className={styles.infoContainer}>
        {showTag && hasTagProps(restProps) && (
          <Tag {...restProps.tagProps} className={styles.tag}>
            <Caption UNSAFE_className={styles.tagColor}>
              {restProps.tagProps.text}
            </Caption>
          </Tag>
        )}
        {children}
      </Grid>
    </Card>
  );
}

StatusCard.Heading = function StatusCardHeading({
  children
}: PropsWithChildren) {
  return <Body UNSAFE_className={styles.cardHeading}>{children}</Body>;
};

type TextProps = { extraClasses?: string } & Pick<
  BodyProps<'span'>,
  'weight' | 'size'
>;

StatusCard.Text = function StatusCardText({
  children,
  extraClasses = '',
  ...restProps
}: PropsWithChildren<TextProps>) {
  return (
    <Body
      UNSAFE_className={clsx(styles.cardText, extraClasses)}
      size="small"
      {...restProps}
    >
      {children}
    </Body>
  );
};

type CaptionTextProps = { extraClasses?: string } & Pick<
  CaptionProps<'span'>,
  'weight'
>;

StatusCard.Caption = function StatusCardCaption({
  extraClasses = '',
  children,
  ...restProps
}: PropsWithChildren<CaptionTextProps>) {
  return (
    <Caption UNSAFE_className={extraClasses} {...restProps}>
      {children}
    </Caption>
  );
};

type RowProps = { extraClasses?: string };

StatusCard.Row = function StatusCardRow({
  children,
  extraClasses = ''
}: PropsWithChildren<RowProps>) {
  return (
    <Grid className={clsx(styles.rowContainer, extraClasses)}>{children}</Grid>
  );
};

StatusCard.Column = function StatusCardColumn({ children }: PropsWithChildren) {
  return <Grid className={styles.columnContainer}>{children}</Grid>;
};

type SubTextProps = { extraClasses?: string };

StatusCard.SubText = function StatusCardSubText({
  extraClasses = '',
  children
}: PropsWithChildren<SubTextProps>) {
  return (
    <Caption UNSAFE_className={clsx(styles.cardSubText, extraClasses)}>
      {children}
    </Caption>
  );
};

export default StatusCard;
